import { useEffect, useState, useCallback } from "react";
import { http, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";

const EMPTY = {
  name: "", room_number: "", unit_type: "Room", description: "", bed_type: "", view: "",
  capacity: 0, max_adults: 0, max_children: 0, base_price: 0, amenities: "",
  sort_order: 0, show_on_website: true, available_for_booking: true, active: true,
};
const UNIT_TYPES = ["Room", "Cottage", "Cabin", "Villa Unit", "Other"];

const toPayload = (r) => ({
  name: r.name || "", room_number: r.room_number || "", unit_type: r.unit_type || "Room",
  description: r.description || "", bed_type: r.bed_type || "", view: r.view || "",
  capacity: Number(r.capacity) || 0, max_adults: Number(r.max_adults) || 0, max_children: Number(r.max_children) || 0,
  base_price: Number(r.base_price) || 0,
  amenities: Array.isArray(r.amenities) ? r.amenities : String(r.amenities || "").split(",").map((s) => s.trim()).filter(Boolean),
  sort_order: Number(r.sort_order) || 0,
  show_on_website: r.show_on_website !== false, available_for_booking: r.available_for_booking !== false,
  active: r.active !== false,
});

export default function RoomsManager() {
  const [rooms, setRooms] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | room.id
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => http.get("/admin/rooms/all").then((r) => setRooms(r.data.rooms)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setForm(EMPTY); setEditing("new"); };
  const startEdit = (r) => { setForm({ ...toPayload(r), amenities: (r.amenities || []).join(", ") }); setEditing(r.id); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Room name is required");
    setBusy(true);
    try {
      const payload = { ...form, amenities: String(form.amenities).split(",").map((s) => s.trim()).filter(Boolean) };
      if (editing === "new") {
        await http.post("/admin/rooms", payload);
        toast.success("Accommodation unit added");
      } else {
        await http.put(`/admin/rooms/${editing}`, payload);
        toast.success("Saved");
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const quickToggle = async (r, patch) => {
    try {
      await http.put(`/admin/rooms/${r.id}`, { ...toPayload(r), ...patch });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="rooms-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-serif text-2xl">Accommodation Units ({rooms.length})</h3>
        <button data-testid="room-add-btn" onClick={startNew} className="btn-primary flex items-center gap-2"><Plus size={16} /> Add Unit</button>
      </div>

      {editing && (
        <div className="card" data-testid="room-form">
          <h3 className="font-serif text-2xl mb-4">{editing === "new" ? "New Accommodation Unit" : "Edit Unit"}</h3>
          <div className="grid md:grid-cols-3 gap-3">
            <input data-testid="room-form-name" placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="room-form-number" placeholder="Internal room no." value={form.room_number} onChange={(e) => setForm({ ...form, room_number: e.target.value })} />
            <select data-testid="room-form-type" value={form.unit_type} onChange={(e) => setForm({ ...form, unit_type: e.target.value })}>
              {UNIT_TYPES.map((t) => <option key={t}>{t}</option>)}
            </select>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Max adults</label><input data-testid="room-form-adults" type="number" min="0" value={form.max_adults} onChange={(e) => setForm({ ...form, max_adults: e.target.value })} /></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Max children</label><input data-testid="room-form-children" type="number" min="0" value={form.max_children} onChange={(e) => setForm({ ...form, max_children: e.target.value })} /></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Max total guests</label><input data-testid="room-form-capacity" type="number" min="0" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Base / nightly price (₹)</label><input data-testid="room-form-price" type="number" min="0" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} /></div>
            <input data-testid="room-form-bed" placeholder="Bed type" value={form.bed_type} onChange={(e) => setForm({ ...form, bed_type: e.target.value })} />
            <input data-testid="room-form-view" placeholder="View" value={form.view} onChange={(e) => setForm({ ...form, view: e.target.value })} />
            <input data-testid="room-form-amenities" placeholder="Amenities (comma separated)" value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} className="md:col-span-2" />
            <div><label className="text-xs" style={{ color: "var(--muted)" }}>Display order</label><input data-testid="room-form-order" type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} /></div>
            <textarea data-testid="room-form-desc" rows="2" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="md:col-span-3" />
          </div>
          <div className="flex flex-wrap gap-6 mt-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" style={{ width: "auto" }} data-testid="room-form-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" style={{ width: "auto" }} data-testid="room-form-bookable" checked={form.available_for_booking} onChange={(e) => setForm({ ...form, available_for_booking: e.target.checked })} /> Available for booking</label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" style={{ width: "auto" }} data-testid="room-form-website" checked={form.show_on_website} onChange={(e) => setForm({ ...form, show_on_website: e.target.checked })} /> Show on website</label>
          </div>
          <div className="flex gap-3 mt-5">
            <button data-testid="room-form-save" onClick={save} disabled={busy} className="btn-primary">{busy ? "Saving…" : "Save Unit"}</button>
            <button data-testid="room-form-cancel" onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
          </div>
        </div>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left border-b" style={{ borderColor: "var(--border)" }}>
            <th className="p-2">Name</th><th>Type</th><th>Sleeps</th><th>Price</th><th>Booking</th><th>Website</th><th>Status</th><th></th>
          </tr></thead>
          <tbody>
            {rooms.map((r) => (
              <tr key={r.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`room-row-${r.id}`}>
                <td className="p-2 font-medium">{r.name}<div className="text-xs" style={{ color: "var(--muted)" }}>{r.room_number && `#${r.room_number}`}</div></td>
                <td>{r.unit_type || "Room"}</td>
                <td>{r.capacity || r.max_adults || "—"}</td>
                <td>{r.base_price ? `₹${r.base_price}` : "—"}</td>
                <td><button data-testid={`room-bookable-${r.id}`} onClick={() => quickToggle(r, { available_for_booking: r.available_for_booking === false })} className={r.available_for_booking !== false ? "badge-pine" : "badge-terracotta"}>{r.available_for_booking !== false ? "Bookable" : "Blocked"}</button></td>
                <td><button data-testid={`room-website-${r.id}`} onClick={() => quickToggle(r, { show_on_website: r.show_on_website === false })} className={r.show_on_website !== false ? "badge-pine" : "badge-terracotta"}>{r.show_on_website !== false ? "Shown" : "Hidden"}</button></td>
                <td><button data-testid={`room-active-${r.id}`} onClick={() => quickToggle(r, { active: r.active === false })} className={r.active !== false ? "badge-pine" : "badge-terracotta"}>{r.active !== false ? "Active" : "Inactive"}</button></td>
                <td><button data-testid={`room-edit-${r.id}`} onClick={() => startEdit(r)} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}><Pencil size={13} /></button></td>
              </tr>
            ))}
            {rooms.length === 0 && <tr><td colSpan="8" className="p-4 text-sm" style={{ color: "var(--muted)" }}>No units yet.</td></tr>}
          </tbody>
        </table>
        <p className="text-xs mt-4" style={{ color: "var(--muted)" }}>
          Units are never permanently deleted. Deactivating keeps all historical bookings, stays, bills and payments intact while removing the unit from new bookings.
        </p>
      </div>
    </div>
  );
}
